import { useId, useState, type FormEvent, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { useToast } from '@/components/ui/toast';

import { registrationError } from '../presentation';
import type { LmsSearchRow } from '../types';
import { useLinkLmsDocument, useLmsSearch } from '../use-document-registration';
import { LmsResults } from './LmsResults';

// Vincula el documento del LMS (archivo y versiones) a un documento nuevo del QMS.
// Calidad crea el documento en el LMS y acá lo busca y lo asocia. La búsqueda corre
// al enviar (consulta remota): no se dispara una llamada por cada tecla.

const MIN_TERM = 2;

export function LinkLmsModal({
  entryId,
  code,
  onClose,
}: {
  entryId: string;
  code: string;
  onClose: () => void;
}): ReactElement {
  const inputId = useId();
  const mutation = useLinkLmsDocument(entryId);
  const { showToast } = useToast();
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  const [selected, setSelected] = useState<LmsSearchRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const results = useLmsSearch(term);
  const canSearch = text.trim().length >= MIN_TERM;

  function search(e: FormEvent): void {
    e.preventDefault();
    if (canSearch) {
      setSelected(null);
      const next = text.trim();
      // Buscar de nuevo el mismo término vuelve a consultar el LMS (el documento
      // pudo crearse recién).
      if (next === term) {
        void results.refetch();
      } else {
        setTerm(next);
      }
    }
  }

  async function confirm(): Promise<void> {
    if (selected === null || mutation.isPending) {
      return;
    }
    setError(null);
    try {
      await mutation.mutateAsync(selected.id);
      showToast(`Archivo ${selected.code} vinculado a ${code}`);
      onClose();
    } catch (err) {
      setError(registrationError(err));
    }
  }

  return (
    <ModalShell
      open
      title="Vincular archivo del LMS"
      wide
      busy={mutation.isPending}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn mut sm"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn sm"
            onClick={() => void confirm()}
            disabled={selected === null || mutation.isPending}
          >
            {mutation.isPending ? 'Vinculando…' : 'Vincular'}
          </button>
        </>
      }
    >
      <p className="modal-body">
        Busca el documento que Calidad creó en el LMS para <span className="mono">{code}</span>. Su
        archivo será el que se edita y se aprueba.
      </p>
      <div className="form-grid">
        <form onSubmit={search} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div className="fld" style={{ flex: 1 }}>
            <label htmlFor={inputId} className="fld-lab">
              Buscar en el LMS
            </label>
            <input
              id={inputId}
              className="selector fw"
              value={text}
              maxLength={100}
              placeholder="Código o nombre"
              autoComplete="off"
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          <button type="submit" className="btn mut sm" disabled={!canSearch}>
            Buscar
          </button>
        </form>
        <LmsResults
          state={results}
          entryId={entryId}
          selected={selected?.id ?? null}
          onSelect={setSelected}
        />
        {error && (
          <p className="fld-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}
